import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  CheckCircle,
  AutoAwesome,
  TrendingUp,
  Psychology,
  Schedule,
  Notifications,
  Send,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  LocationOn,
  Language,
  Settings,
  Dashboard,
  People,
  Analytics
} from '@mui/icons-material';

const ClientDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Client data state
  const [clientData, setClientData] = useState({
    businessName: 'Myrtle Beach Real Estate',
    contactName: 'John Smith',
    email: 'john@myrtlebeachrealestate.com',
    phone: '+1-843-555-1234',
    industry: 'real-estate',
    service: 'Real estate agent',
    autoContact: true,
    platforms: ['linkedin', 'facebook'],
    keywords: ['buying', 'selling', 'mortgage'],
    geoTargeting: {
      enabled: true,
      locations: ['myrtle beach, sc', 'north myrtle beach, sc'],
      radius: 50
    }
  });

  const [customKeyword, setCustomKeyword] = useState('');
  const [customLocation, setCustomLocation] = useState('');

  const industries = [
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'attorney', label: 'Attorney' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'technology', label: 'Technology' }
  ];

  const platforms = [
    { value: 'linkedin', label: 'LinkedIn', description: 'Professional networking and B2B leads' },
    { value: 'facebook', label: 'Facebook', description: 'Local business and community engagement' },
    { value: 'instagram', label: 'Instagram', description: 'Visual content and brand awareness' },
    { value: 'twitter', label: 'Twitter', description: 'Real-time conversations and industry discussions' }
  ];

  const popularLocations = [
    // South Carolina
    { value: 'myrtle beach, sc', label: 'Myrtle Beach, SC', category: 'South Carolina' },
    { value: 'north myrtle beach, sc', label: 'North Myrtle Beach, SC', category: 'South Carolina' },
    { value: 'surfside beach, sc', label: 'Surfside Beach, SC', category: 'South Carolina' },
    { value: 'charleston, sc', label: 'Charleston, SC', category: 'South Carolina' },
    { value: 'columbia, sc', label: 'Columbia, SC', category: 'South Carolina' },
    { value: 'greenville, sc', label: 'Greenville, SC', category: 'South Carolina' },
    // Major US Cities
    { value: 'new york, ny', label: 'New York, NY', category: 'Major US Cities' },
    { value: 'los angeles, ca', label: 'Los Angeles, CA', category: 'Major US Cities' },
    { value: 'chicago, il', label: 'Chicago, IL', category: 'Major US Cities' },
    { value: 'houston, tx', label: 'Houston, TX', category: 'Major US Cities' },
    { value: 'miami, fl', label: 'Miami, FL', category: 'Major US Cities' },
    { value: 'atlanta, ga', label: 'Atlanta, GA', category: 'Major US Cities' },
    // International
    { value: 'london, uk', label: 'London, UK', category: 'International' },
    { value: 'toronto, canada', label: 'Toronto, Canada', category: 'International' },
    { value: 'sydney, australia', label: 'Sydney, Australia', category: 'International' },
    { value: 'dubai, uae', label: 'Dubai, UAE', category: 'International' },
    { value: 'tokyo, japan', label: 'Tokyo, Japan', category: 'International' }
  ];

  // Mock lead statistics
  const [leadStats] = useState({
    totalLeads: 47,
    thisMonth: 12,
    thisWeek: 3,
    conversionRate: 23.4,
    averageValue: 1250,
    topLocations: [
      { location: 'Myrtle Beach, SC', leads: 18 },
      { location: 'North Myrtle Beach, SC', leads: 12 },
      { location: 'Surfside Beach, SC', leads: 8 }
    ]
  });

  const handleInputChange = (field, value) => {
    setClientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addKeyword = () => {
    if (customKeyword.trim() && !clientData.keywords.includes(customKeyword.trim())) {
      setClientData(prev => ({
        ...prev,
        keywords: [...prev.keywords, customKeyword.trim()]
      }));
      setCustomKeyword('');
    }
  };

  const removeKeyword = (keyword) => {
    setClientData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const togglePlatform = (platform) => {
    setClientData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const toggleLocation = (location) => {
    setClientData(prev => ({
      ...prev,
      geoTargeting: {
        ...prev.geoTargeting,
        locations: prev.geoTargeting.locations.includes(location)
          ? prev.geoTargeting.locations.filter(l => l !== location)
          : [...prev.geoTargeting.locations, location]
      }
    }));
  };

  const addCustomLocation = () => {
    if (customLocation.trim() && !clientData.geoTargeting.locations.includes(customLocation.trim().toLowerCase())) {
      setClientData(prev => ({
        ...prev,
        geoTargeting: {
          ...prev.geoTargeting,
          locations: [...prev.geoTargeting.locations, customLocation.trim().toLowerCase()]
        }
      }));
      setCustomLocation('');
    }
  };

  const removeLocation = (location) => {
    setClientData(prev => ({
      ...prev,
      geoTargeting: {
        ...prev.geoTargeting,
        locations: prev.geoTargeting.locations.filter(l => l !== location)
      }
    }));
  };

  const toggleGeoTargeting = () => {
    setClientData(prev => ({
      ...prev,
      geoTargeting: {
        ...prev.geoTargeting,
        enabled: !prev.geoTargeting.enabled
      }
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Call API to update client settings
      const response = await fetch('https://social-media-backend.andrewrkloc.workers.dev/api/leads/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Settings updated successfully! Your lead generation will use the new settings immediately.');
        setIsEditing(false);
      } else {
        setErrorMessage(data.error || 'Failed to update settings.');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      setErrorMessage('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original data (in real app, fetch from API)
    setIsEditing(false);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const renderOverviewTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Lead Generation Overview
        </Typography>
      </Grid>

      {/* Statistics Cards */}
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Leads
            </Typography>
            <Typography variant="h4">
              {leadStats.totalLeads}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              This Month
            </Typography>
            <Typography variant="h4">
              {leadStats.thisMonth}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Conversion Rate
            </Typography>
            <Typography variant="h4">
              {leadStats.conversionRate}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Avg Lead Value
            </Typography>
            <Typography variant="h4">
              ${leadStats.averageValue}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Current Settings Summary */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Settings
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><Business /></ListItemIcon>
                <ListItemText 
                  primary="Industry" 
                  secondary={industries.find(i => i.value === clientData.industry)?.label} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Language /></ListItemIcon>
                <ListItemText 
                  primary="Platforms" 
                  secondary={clientData.platforms.join(', ')} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationOn /></ListItemIcon>
                <ListItemText 
                  primary="Target Locations" 
                  secondary={clientData.geoTargeting.enabled ? `${clientData.geoTargeting.locations.length} locations` : 'Disabled'} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><AutoAwesome /></ListItemIcon>
                <ListItemText 
                  primary="Auto Contact" 
                  secondary={clientData.autoContact ? 'Enabled' : 'Disabled'} 
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Performing Locations */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Performing Locations
            </Typography>
            {leadStats.topLocations.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>{item.location}</Typography>
                <Typography variant="body2" color="primary">
                  {item.leads} leads
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderSettingsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Lead Generation Settings
          </Typography>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setIsEditing(true)}
            >
              Edit Settings
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={isLoading}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </Grid>

      {successMessage && (
        <Grid item xs={12}>
          <Alert severity="success" onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        </Grid>
      )}

      {errorMessage && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        </Grid>
      )}

      {/* Business Information */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Business Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={clientData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={clientData.contactName}
                  onChange={(e) => handleInputChange('contactName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={clientData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={clientData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Industry and Keywords */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Industry & Keywords
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Industry</InputLabel>
              <Select
                value={clientData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                label="Industry"
                disabled={!isEditing}
              >
                {industries.map((industry) => (
                  <MenuItem key={industry.value} value={industry.value}>
                    {industry.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" gutterBottom>
              Keywords ({clientData.keywords.length})
            </Typography>
            
            {isEditing && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Add keyword"
                  value={customKeyword}
                  onChange={(e) => setCustomKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                />
                <Button
                  variant="contained"
                  onClick={addKeyword}
                  disabled={!customKeyword.trim()}
                >
                  Add
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {clientData.keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  onDelete={isEditing ? () => removeKeyword(keyword) : undefined}
                  color="primary"
                  variant="filled"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Platforms */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Social Media Platforms
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We'll monitor these platforms for potential leads
            </Typography>
            
            {platforms.map((platform) => (
              <Box
                key={platform.value}
                sx={{
                  p: 2,
                  mb: 1,
                  border: '1px solid',
                  borderColor: clientData.platforms.includes(platform.value) ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  cursor: isEditing ? 'pointer' : 'default',
                  '&:hover': isEditing ? { borderColor: 'primary.main' } : {}
                }}
                onClick={isEditing ? () => togglePlatform(platform.value) : undefined}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle 
                    color={clientData.platforms.includes(platform.value) ? 'primary' : 'disabled'} 
                  />
                  <Box>
                    <Typography variant="h6">{platform.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {platform.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Location Targeting */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Location Targeting
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={clientData.geoTargeting.enabled}
                  onChange={toggleGeoTargeting}
                  disabled={!isEditing}
                />
              }
              label="Enable location-based lead filtering"
            />

            {clientData.geoTargeting.enabled && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Popular Locations
                </Typography>
                
                {['South Carolina', 'Major US Cities', 'International'].map((category) => (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {category}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {popularLocations
                        .filter(location => location.category === category)
                        .map((location) => (
                          <Chip
                            key={location.value}
                            label={location.label}
                            onClick={isEditing ? () => toggleLocation(location.value) : undefined}
                            color={clientData.geoTargeting.locations.includes(location.value) ? 'primary' : 'default'}
                            variant={clientData.geoTargeting.locations.includes(location.value) ? 'filled' : 'outlined'}
                          />
                        ))}
                    </Box>
                  </Box>
                ))}

                {isEditing && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Add Custom Location
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="Custom location"
                        value={customLocation}
                        onChange={(e) => setCustomLocation(e.target.value)}
                        placeholder="e.g., Miami, FL or London, UK"
                        onKeyPress={(e) => e.key === 'Enter' && addCustomLocation()}
                      />
                      <Button
                        variant="contained"
                        onClick={addCustomLocation}
                        disabled={!customLocation.trim()}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                )}

                {clientData.geoTargeting.locations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Locations ({clientData.geoTargeting.locations.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {clientData.geoTargeting.locations.map((location) => (
                        <Chip
                          key={location}
                          label={location}
                          onDelete={isEditing ? () => removeLocation(location) : undefined}
                          color="primary"
                          variant="filled"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Contact Preferences */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contact Preferences
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={clientData.autoContact}
                  onChange={(e) => handleInputChange('autoContact', e.target.checked)}
                  disabled={!isEditing}
                />
              }
              label="Automatically contact leads on my behalf"
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
              We'll send personalized messages to qualified leads using your business information.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderLeadsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Recent Leads
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your most recent lead notifications and their status
        </Typography>
      </Grid>

      {/* Mock recent leads */}
      {[
        {
          id: 1,
          name: 'Sarah Johnson',
          company: 'Johnson Real Estate',
          location: 'Myrtle Beach, SC',
          source: 'LinkedIn',
          score: 85,
          status: 'Hot',
          date: '2024-01-15'
        },
        {
          id: 2,
          name: 'Mike Thompson',
          company: 'Thompson Properties',
          location: 'North Myrtle Beach, SC',
          source: 'Facebook',
          score: 72,
          status: 'Warm',
          date: '2024-01-14'
        },
        {
          id: 3,
          name: 'Lisa Chen',
          company: 'Chen Realty Group',
          location: 'Surfside Beach, SC',
          source: 'Instagram',
          score: 91,
          status: 'Hot',
          date: '2024-01-13'
        }
      ].map((lead) => (
        <Grid item xs={12} key={lead.id}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{lead.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {lead.company} • {lead.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Source: {lead.source} • Score: {lead.score}/100
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Chip 
                    label={lead.status} 
                    color={lead.status === 'Hot' ? 'error' : 'warning'}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {lead.date}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Client Dashboard
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<Dashboard />} label="Overview" />
          <Tab icon={<Settings />} label="Settings" />
          <Tab icon={<People />} label="Recent Leads" />
        </Tabs>
      </Paper>

      {activeTab === 0 && renderOverviewTab()}
      {activeTab === 1 && renderSettingsTab()}
      {activeTab === 2 && renderLeadsTab()}
    </Box>
  );
};

export default ClientDashboard;
